import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormField } from '../components/FormField';
import { Button } from '../components/Button';
import { Turnstile } from '../components/Turnstile';
import { signup } from '../api/auth';
import { ApiError } from '../api/client';
import { PRIVACY_URL, TERMS_URL } from '../config';

// Cloudflare Turnstile site key from build-time env (Vite). Set
// VITE_TURNSTILE_SITEKEY in the build environment to enable the widget;
// leave empty during pre-launch / local dev (the backend likewise no-ops
// when TURNSTILE_SECRET is unset).
const TURNSTILE_SITEKEY = (import.meta as unknown as { env?: Record<string, string> })
  .env?.VITE_TURNSTILE_SITEKEY ?? '';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PW_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;

export function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!EMAIL_RE.test(email.trim())) next.email = 'Enter a valid email address.';
    if (!PW_RE.test(password)) next.password = 'At least 12 characters, with upper, lower, digit and symbol.';
    if (!consent) next.consent = 'You must confirm parental consent.';
    // If the sitekey is configured but no token has been issued, the
    // widget hasn't fired its callback yet — block the submit. The
    // widget challenges invisibly so this is normally a sub-second
    // wait; we show the form-level error if the user is fast enough
    // to click Submit first.
    if (TURNSTILE_SITEKEY && !turnstileToken) {
      next.form = 'Please wait a moment while we verify you\'re human, then try again.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      await signup({
        email: email.trim().toLowerCase(),
        password,
        parentalConsent: true,
        firstName: firstName.trim() || undefined,
        turnstileToken,
      });
      navigate(`/verify?email=${encodeURIComponent(email.trim().toLowerCase())}`);
    } catch (err) {
      const e2 = err as ApiError;
      setErrors(e2.fields ?? { form: e2.message || 'Something went wrong.' });
      // A captcha token is single-use — if signup failed, clear our
      // state so the user gets a fresh challenge on next attempt.
      setTurnstileToken(undefined);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h1 style={{ marginBottom: 24 }}>Create account</h1>

      <FormField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
        placeholder="your@email.com"
        error={errors.email}
        required
      />
      <FormField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        placeholder="At least 12 characters"
        hint="At least 12 characters with upper, lower, digit and symbol."
        error={errors.password}
        required
      />
      <FormField
        label="Your first name (optional)"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        autoComplete="given-name"
        placeholder="So we know what to call you"
      />

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 8, marginBottom: 12, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          style={{ width: 20, height: 20, accentColor: 'var(--green)', marginTop: 3 }}
        />
        <span style={{ fontSize: 14, lineHeight: 1.5 }}>
          I confirm I am a parent or legal guardian and consent to MyFootballJournal processing my child's data, under our{' '}
          <a href={PRIVACY_URL} style={{ color: 'var(--green)' }}>Privacy notice</a>{' '}and{' '}
          <a href={TERMS_URL} style={{ color: 'var(--green)' }}>Terms</a>.
        </span>
      </label>
      {errors.consent ? <p style={{ color: 'var(--error)', fontSize: 14, marginTop: 4 }}>{errors.consent}</p> : null}

      <Turnstile
        sitekey={TURNSTILE_SITEKEY}
        onVerify={setTurnstileToken}
        onExpire={() => setTurnstileToken(undefined)}
        onError={() => setTurnstileToken(undefined)}
      />

      {errors.form ? <p style={{ color: 'var(--error)', fontSize: 14, marginTop: 8 }}>{errors.form}</p> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
        <Button label="Create account" type="submit" loading={submitting} />
        <Link to="/login" style={{ textAlign: 'center', color: 'var(--green)', fontSize: 14, padding: 8 }}>
          I already have an account
        </Link>
      </div>
    </form>
  );
}
